import { ConnectionManager, type ConnectionState } from '@bn-games/room-client';
import { getPresenceToken, getWsTicket } from '@bn-games/auth-client';
import type {
  LobbyChannelClientMessage,
  LobbyChannelServerMessage,
  LobbyPresenceEntry,
} from './types.js';
import { isOnlineGame } from './types.js';
import type { PresenceStatus } from './types.js';

/**
 * Inside T15's 5-minute presence-token TTL, leaving margin for network
 * latency and the interval's own jitter — a socket can stay open for hours
 * with no reconnect event, so nothing else re-authorizes this connection's
 * PRESENCE_QUERY access unless this fires on its own schedule.
 */
const HELLO_REFRESH_INTERVAL_MS = 4 * 60 * 1000;

/** How often the watched-id list is re-queried. PRESENCE_QUERY is pure
 * request/response — the server never pushes a status change on its own. */
const PRESENCE_QUERY_INTERVAL_MS = 30 * 1000;

export type PresenceListener = (statuses: readonly PresenceStatus[]) => void;
export type InvitePushListener = (inviteId: string) => void;

function toPresenceStatus(entry: LobbyPresenceEntry): PresenceStatus {
  return {
    userId: entry.userId,
    state: entry.state,
    game: entry.game !== null && isOnlineGame(entry.game) ? entry.game : null,
    lastSeenAt: entry.lastSeenAt,
  };
}

/**
 * The persistent `/ws/lobby` channel, client side (F5 milestone 2, T19).
 * Wraps `ConnectionManager` with three things it doesn't know how to do on
 * its own: fetching and periodically refreshing the presence-token
 * authorization (`LOBBY_HELLO`), a query/refresh loop for a caller-supplied
 * set of watched user ids, and surfacing pushed invite ids to a listener.
 *
 * The underlying socket's own identity is authenticated the ordinary way —
 * `setTicketProvider(getWsTicket)`, the same WS ticket every per-game socket
 * uses — which is why this class needs no ticket/identity concept of its
 * own beyond the presence-token refresh loop.
 */
export class LobbySocket {
  private readonly connection: ConnectionManager<LobbyChannelClientMessage, LobbyChannelServerMessage>;
  private watchedIds: readonly string[] = [];
  private helloTimer: ReturnType<typeof setInterval> | null = null;
  private queryTimer: ReturnType<typeof setInterval> | null = null;
  private readonly presenceListeners = new Set<PresenceListener>();
  private readonly inviteListeners = new Set<InvitePushListener>();

  constructor(serverUrl: string) {
    this.connection = new ConnectionManager<LobbyChannelClientMessage, LobbyChannelServerMessage>({
      serverUrl,
    });
    this.connection.setTicketProvider(getWsTicket);
    this.connection.onStateChange(() => this.syncWatchLoops());
    this.connection.onMessage((message) => this.handleMessage(message));
  }

  connect(): void {
    this.connection.connect();
  }

  disconnect(): void {
    this.clearTimers();
    this.connection.disconnect();
  }

  getState(): ConnectionState {
    return this.connection.getState();
  }

  /**
   * Replaces the full set of ids to keep live presence for. Watching is what
   * turns on `LOBBY_HELLO` and its refresh: invite push needs no presence
   * token, and an always-on hello would wake the server every refresh
   * interval for every signed-in tab. Queries immediately when the loops were
   * already running; a first watch queries right after its hello, and before
   * connect the loops start as soon as the socket reaches "connected".
   */
  watch(userIds: readonly string[]): void {
    const wasRunning = this.helloTimer !== null;
    this.watchedIds = userIds;
    this.syncWatchLoops();
    if (wasRunning) this.sendQuery();
  }

  onPresence(listener: PresenceListener): () => void {
    this.presenceListeners.add(listener);
    return () => this.presenceListeners.delete(listener);
  }

  onInvitePush(listener: InvitePushListener): () => void {
    this.inviteListeners.add(listener);
    return () => this.inviteListeners.delete(listener);
  }

  /**
   * Sends an already-created invite's id to its recipient's live lobby
   * socket, if any. An optimisation only — the caller has already written
   * the durable record via `POST /invites` (`sendInvite` in `invites.ts`)
   * before calling this; see the plan's trust boundary for why the relay
   * carries only the id.
   */
  sendInvite(inviteId: string, toUserId: string): void {
    this.connection.send({ type: 'INVITE_SEND', inviteId, toUserId });
  }

  // Runs the hello + query loops exactly while connected with a non-empty
  // watch list. The first query waits for the first hello, since the server
  // answers nothing outside a hello's `can` list.
  private syncWatchLoops(): void {
    const shouldRun = this.connection.getState() === 'connected' && this.watchedIds.length > 0;
    if (!shouldRun) {
      this.clearTimers();
      return;
    }
    if (this.helloTimer !== null) return;
    this.helloTimer = setInterval(() => void this.sendHello(), HELLO_REFRESH_INTERVAL_MS);
    this.queryTimer = setInterval(() => this.sendQuery(), PRESENCE_QUERY_INTERVAL_MS);
    void this.sendHello().then(() => this.sendQuery());
  }

  private async sendHello(): Promise<void> {
    const token = await getPresenceToken();
    // The socket may have dropped (or the watch list emptied) during the
    // token fetch; a queued hello would otherwise flush into a later session.
    if (token === null || this.helloTimer === null) return;
    this.connection.send({ type: 'LOBBY_HELLO', token });
  }

  private sendQuery(): void {
    if (this.watchedIds.length === 0) return;
    if (this.connection.getState() !== 'connected') return;
    this.connection.send({ type: 'PRESENCE_QUERY', userIds: this.watchedIds });
  }

  private clearTimers(): void {
    if (this.helloTimer !== null) {
      clearInterval(this.helloTimer);
      this.helloTimer = null;
    }
    if (this.queryTimer !== null) {
      clearInterval(this.queryTimer);
      this.queryTimer = null;
    }
  }

  private handleMessage(message: LobbyChannelServerMessage): void {
    if (message.type === 'PRESENCE_STATE') {
      const statuses = message.statuses.map(toPresenceStatus);
      for (const listener of this.presenceListeners) listener(statuses);
      return;
    }
    if (message.type === 'INVITE_PUSH') {
      for (const listener of this.inviteListeners) listener(message.inviteId);
    }
  }
}
